<script lang="ts" setup>
import type { Product } from '@/lib/types/Product'
import formatMoney from '@/lib/formatters/money'
import PurchaseStarterKitTabOnce from './PurchaseStarterKitTabOnce.vue'
import PurchaseStarterKitTabSubscribe from './PurchaseStarterKitTabSubscribe.vue'
import Select from '@/components/form-fields/Select.vue'
import type { PurchaseData } from '@/lib/types/PurchaseData'
import type { User } from '@/lib/types/User'
import { usePurchaseState } from '@/components/lib/PurchaseState'
import type { WorkState } from '@/lib/types/WorkState'
import { computed } from 'vue'
import { shipping } from '@/lib/constants'

const props = withDefaults(
  defineProps<{
    product: Product
    user: User
    initialQuantity: number
    initialSellingPlan?: string
    initialWhen: 'now' | 'next'
    initialVariantId?: string
    purchaseState: WorkState
    loginLink: string
    customerPortalLink: string
    nextOrderLink: string
    immediateCheckout?: boolean
  }>(),
  {
    initialQuantity: 1,
    initialWhen: 'now',
  },
)

const emit = defineEmits<{
  (e: 'purchase', p: PurchaseData): void
  (e: 'interaction'): void
  (e: 'variantChange', variantId: string): void
  (e: 'viewProduct', productId: string): void
}>()

const freeDelivery = computed(
  () =>
    !!(
      selectedVariant.value.weight &&
      selectedVariant.value.weight >= shipping.free_threshold_grams
    ),
)

const {
  selectedVariantId,
  selectedVariant,
  selectedSellingPlanId,
  hasVariants,
  isSubscriptionProduct,
  price,
  subscribePrice,
  pricePer,
  savePercent,
  quantity,
  when,
  subscriptionToThisProduct,
  emitPurchaseEvent,
  url,
  nextOrderDate,
} = usePurchaseState(props, emit)
</script>

<template>
  <form class="Purchase" @submit.prevent>
    <div class="Stack">
      <div v-if="hasVariants" class="Purchase__VariantSelector">
        <span
          v-if="product.variantOptionLabel"
          class="Purchase__VariantSelectorLabel"
        >
          Choose {{ product.variantOptionLabel.toLowerCase() }}:
        </span>
        <div class="Purchase__VariantSelectorDropdown">
          <Select
            :value="selectedVariantId"
            :disabled="purchaseState === 'IN_PROGRESS'"
            @change="selectedVariantId = $event"
          >
            <option
              v-for="variantId in Object.keys(product.variants)"
              :key="variantId"
              :value="variantId"
            >
              {{ product.variants[variantId].title }} –
              {{ formatMoney(product.variants[variantId].price) }}
            </option>
          </Select>
        </div>
      </div>
      <PurchaseStarterKitTabSubscribe
        v-if="isSubscriptionProduct && selectedSellingPlanId !== null"
        :price="price"
        :price-per="pricePer"
        :compare-at-price="selectedVariant.compareAtPrice"
        :subscribe-price="subscribePrice"
        :save-percent="savePercent"
        :quantity="quantity"
        :when="when"
        :selected-selling-plan-id="selectedSellingPlanId"
        :selling-plan-allocations="selectedVariant.sellingPlanAllocations"
        :is-logged-in="user.isLoggedIn"
        :user-load-state="user.loadState"
        :next-order-date="nextOrderDate"
        :login-link="loginLink"
        :subscription-to-this-product="subscriptionToThisProduct"
        :purchase-state="purchaseState"
        :customer-portal-link="customerPortalLink"
        :next-order-link="nextOrderLink"
        :available="product.available"
        :pre-sale="product.preSale"
        :waitlist-page-url="product.waitlistPageUrl"
        :selected-variant-id="selectedVariantId"
        :has-variants="hasVariants"
        :url="url"
        :in-tab="true"
        :show-inline-variant-selector="false"
        :has-dispensers="
          !/toilet tissue/i.test(product.title) &&
          product.id.toString() !== '7663577399358'
        "
        :is-trial="product.id.toString() === '7322083655742'"
        @quantity:changed="quantity = $event"
        @when:changed="when = $event"
        @selected-selling-plan:changed="selectedSellingPlanId = $event"
        @purchase="emitPurchaseEvent"
        :allow-subscribe="true"
        :variants="product.variants"
        :variant-option-label="product.variantOptionLabel"
        :immediate-checkout="immediateCheckout"
        :free-delivery="freeDelivery"
      />

      <PurchaseStarterKitTabOnce
        v-else
        :price="price"
        :price-per="pricePer"
        :compare-at-price="selectedVariant.compareAtPrice"
        :allow-subscribe="isSubscriptionProduct"
        :subscribe-price="subscribePrice"
        :save-percent="savePercent"
        :quantity="quantity"
        :when="when"
        :selected-selling-plan-id="selectedSellingPlanId"
        :selling-plan-allocations="selectedVariant.sellingPlanAllocations"
        :is-logged-in="user.isLoggedIn"
        :user-load-state="user.loadState"
        :next-order-date="nextOrderDate"
        :login-link="loginLink"
        :subscription-to-this-product="subscriptionToThisProduct"
        :purchase-state="purchaseState"
        :customer-portal-link="customerPortalLink"
        :next-order-link="nextOrderLink"
        :available="product.available"
        :pre-sale="product.preSale"
        :waitlist-page-url="product.waitlistPageUrl"
        :selected-variant-id="selectedVariantId"
        :has-variants="hasVariants"
        :variants="product.variants"
        :url="url"
        :in-tab="true"
        :show-inline-variant-selector="false"
        :variant-option-label="product.variantOptionLabel"
        :has-dispensers="!/toilet tissue/i.test(product.title)"
        :is-trial="product.id.toString() === '7322083655742'"
        @quantity:changed="quantity = $event"
        @when:changed="when = $event"
        @selected-selling-plan:changed="selectedSellingPlanId = $event"
        @purchase="emitPurchaseEvent"
        :immediate-checkout="immediateCheckout"
      />
    </div>
  </form>
</template>

<style>
.TabbedArea__NavItem .Label {
  font-weight: normal;
  margin: -6px 0;
}

.Purchase__FeatureListItem {
  display: flex;
  gap: calc(var(--grid-gap) / 4);

  & > svg {
    flex: 0 0 24px;
    position: relative;
    top: 2px;
  }
}
</style>
